/**
 *
 * @param error
 */
export function formatError(error: any) {
    if (!error) {
      return '';
    }
  
    if (error.stack) {
      return `fileName: '${error.fileName}', name: '${error.name}' lineNumber:${error.lineNumber} columnNumber:${error.columnNumber} message:'${error.message}'.`;
    }
  
    if (error.message) {
      return error.message;
    }
    return JSON.stringify(error);
  }
  
  /**
   *
   * @param text
   * @param leadingCharactersToKeep
   */
  export function concise(text = '', leadingCharactersToKeep = 32) {
    if (text.length > leadingCharactersToKeep) {
      return `${text.substring(0, leadingCharactersToKeep - 1)}...`;
    }
    return text;
    // return text ? (text.length > leadingCharactersToKeep ? text.substring(0, leadingCharactersToKeep - 1) + '...' : text) : '';
  }

  // Field names whose values must never appear verbatim in logs. Matched
  // case-insensitively against any object key during error serialization or
  // direct token summarization (used by `safeStringifyError` and
  // `summarizeTokens`).
  const SENSITIVE_FIELD_PATTERNS: ReadonlyArray<RegExp> = [
    /access_?token/i,
    /refresh_?token/i,
    /id_?token/i,
    /cross_?launch_?token/i,
    /cross_?launch_?call_?token/i,
    /msft_?token/i,
    /msftrefreshtoken/i,
    /webex_?token/i,
    /^assertion$/i,
    /^code$/i,
    /^client_?secret$/i,
    /^password$/i,
    /^authorization$/i,
    /^cookie$/i,
    /^set-cookie$/i,
  ];

  function isSensitiveFieldName(name: string): boolean {
    return SENSITIVE_FIELD_PATTERNS.some((re) => re.test(name));
  }

  /**
   * Replace a secret with a non-reversible diagnostic placeholder. Returned
   * value contains only the byte length so log readers can confirm "the field
   * was populated" without exposing any of its bytes. Returns `<absent>` when
   * the value is null/undefined/empty so missing-credential bugs remain
   * easy to triage from the logs.
   */
  export function redactSecret(value: unknown): string {
    if (value === undefined || value === null || value === '') {
      return '<absent>';
    }
    if (typeof value === 'string') {
      return `<redacted len=${value.length}>`;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return `<redacted type=${typeof value}>`;
    }
    return '<redacted>';
  }

  /**
   * Build a non-leaking, single-line diagnostic summary of an OAuth token
   * bundle. Only field presence, lengths, and non-secret metadata
   * (`expires_in`, `webex_orgId`) are reported; `access_token`,
   * `refresh_token`, `cross_launch_token`, `assertion`, etc. are scrubbed
   * via `redactSecret`. Pass anything (Webex / MS Teams / cross-launch
   * shapes) — unknown fields are reported as keys-only.
   */
  export function summarizeTokens(tokens: unknown): string {
    if (tokens === undefined || tokens === null) {
      return '<absent>';
    }
    if (typeof tokens !== 'object') {
      return `<non-object type=${typeof tokens}>`;
    }
    const obj = tokens as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of Object.keys(obj)) {
      if (isSensitiveFieldName(key)) {
        parts.push(`${key}=${redactSecret(obj[key])}`);
        continue;
      }
      const v = obj[key];
      if (v === undefined || v === null) {
        parts.push(`${key}=<absent>`);
      } else if (typeof v === 'string') {
        // Non-sensitive strings: keep length only to avoid accidental PII
        // leaks (org IDs / user IDs / emails sometimes reach this path).
        parts.push(`${key}=<string len=${v.length}>`);
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        parts.push(`${key}=${String(v)}`);
      } else {
        parts.push(`${key}=<${typeof v}>`);
      }
    }
    return `{${parts.join(', ')}}`;
  }

  /**
   * Safely serialize an error for logging. Designed primarily for Axios
   * errors: `JSON.stringify(axiosError)` invokes `AxiosError.toJSON()` which
   * dumps the request `config` (URL, method, params, headers, body). For
   * OAuth code/refresh exchanges that body contains the authorization code
   * or refresh token, and the headers contain `Authorization: Bearer ...`.
   *
   * This helper preserves the diagnostic shape (status, code, message,
   * request method/URL) while redacting every header value and every body
   * field whose name matches a sensitive pattern.
   */
  export function safeStringifyError(err: unknown): string {
    if (err === undefined || err === null) return '';
    if (typeof err === 'string') return err;
    if (typeof err !== 'object') return String(err);

    const e = err as Record<string, any>;
    const out: Record<string, unknown> = {};

    // Axios error envelope.
    if (e.message) out.message = String(e.message);
    if (e.name) out.name = String(e.name);
    if (e.code) out.code = String(e.code);
    if (e.status !== undefined) out.status = e.status;

    if (e.response && typeof e.response === 'object') {
      const resp = e.response as Record<string, any>;
      out.response = {
        status: resp.status,
        statusText: resp.statusText,
        // Body is intentionally omitted — server responses for token
        // endpoints frequently echo the failed assertion or token back in
        // the error body.
      };
    }

    if (e.config && typeof e.config === 'object') {
      const cfg = e.config as Record<string, any>;
      const safeConfig: Record<string, unknown> = {};
      if (cfg.method) safeConfig.method = String(cfg.method);
      if (cfg.url) safeConfig.url = String(cfg.url);
      if (cfg.baseURL) safeConfig.baseURL = String(cfg.baseURL);
      if (cfg.timeout !== undefined) safeConfig.timeout = cfg.timeout;
      // Headers and data are redacted wholesale: even non-sensitive headers
      // (User-Agent, Content-Type) aren't worth the parse cost of a
      // selective redaction here.
      if (cfg.headers) safeConfig.headers = '<redacted>';
      if (cfg.data !== undefined) safeConfig.data = '<redacted>';
      if (cfg.params !== undefined) safeConfig.params = '<redacted>';
      out.config = safeConfig;
    }

    try {
      return JSON.stringify(out);
    } catch {
      return `<unstringifiable error: ${String(e.message ?? e.name ?? 'unknown')}>`;
    }
  }

  export function downloadMobileLogs() {
    Logger.info(`downloadLog(): download log.`);
    let content = localStorage.getItem('log') || '';
    if (!content) {
        content = "";
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = "msteams-integration.log";

    link.click();
  }
  
  

  export function downloadLogs() {
    let content = localStorage.getItem("log");
    if (!content) {
        content = "";
    }
    const anchorElement = document.createElement("a");
    anchorElement.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(content));
    anchorElement.setAttribute("download", "webex_calling_integration.log");
    anchorElement.style.display = "none";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  }
  
  // Numeric severity used by `shouldEmit` to decide whether a given call
  // site is suppressed by the configured `WEBEX_LOG_LEVEL`. Higher number
  // = more severe; messages whose severity is below the configured floor
  // are dropped completely (no console, no localStorage).
  const LOG_LEVEL_RANK: Readonly<Record<string, number>> = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
  };

  function configuredLogLevelRank(): number {
    // `process.env.WEBEX_LOG_LEVEL` is materialized at build time by
    // `EnvironmentPlugin` in webpack.{prod,dev}.js. Default to `info`
    // when nothing is set so debug noise can never leak into a build that
    // forgot to declare its level.
    const raw = (typeof process !== 'undefined' && process.env && process.env.WEBEX_LOG_LEVEL) || 'info';
    const normalized = String(raw).toLowerCase();
    return LOG_LEVEL_RANK[normalized] ?? LOG_LEVEL_RANK.info;
  }

  export class Logger {
    // Previously declared as `static maxLogSizeInBytes: 500000;`, which is a
    // *type* literal annotation, not an assignment, so the property was
    // `undefined` at runtime and the rotation check below silently never
    // fired (the `localStorage["log"]` buffer grew unbounded).
    static maxLogSizeInBytes = 500_000;
    static isTestEnvironment = process.env.NODE_ENV === 'test';
    private static minLevelRank = configuredLogLevelRank();

    /** Internal: returns true iff `level` should reach console/localStorage. */
    private static shouldEmit(level: string): boolean {
      const rank = LOG_LEVEL_RANK[level] ?? LOG_LEVEL_RANK.info;
      return rank >= Logger.minLevelRank;
    }

    static log(level: string, msg: string) {
      if (!Logger.shouldEmit(level)) {
        return;
      }
      const logMsg: string[] = [];
      logMsg.push(new Date().toISOString(), " ", "[", level, "]", " ", msg, "\n");
      if (level === "error") {
          //console.log(getErrorObject());
      }
      if (!this.isTestEnvironment && level !== "debug") {
          let log = localStorage.getItem("log");
          if (!log) {
              log = "";
          }
          if (log.length > Logger.maxLogSizeInBytes) {
              localStorage.removeItem("log");
              log = "";
          }
          localStorage.setItem("log", log + logMsg.join(""));
      }
      console.log(`${new Date().toISOString()} [${level}] ${msg}\n`);
  }
    static debug(msg: string) {
      Logger.log('debug', msg);
    }
  
    static info(msg: string) {
      Logger.log('info', msg);
    }
  
    static error(msg: string) {
      Logger.log('error', msg);
    }
  }
  
  export enum LOGGER {
    ERROR = "error",
    WARN = "warn",
    INFO = "info",
    LOG = "log",
    TRACE = "trace"
}